import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  useAiAssistantThreadDelete,
  useAiAssistantThreads,
} from "@/queries/use-ai-assistant.query.ts";
import { ApiAiAssistantThread } from "@/types/ai-assistant.types.ts";
import {
  ButtonIcon,
  ConfirmationModal,
  FontAwesomeIcon,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface AiAssistantThreadsProps {
  activeThreadId?: number;
  onOpen: (threadId: number) => void;
  onDeleted: (threadId: number) => void;
}

export function AiAssistantThreads({ activeThreadId, onOpen, onDeleted }: AiAssistantThreadsProps) {
  const { t } = useTranslation();
  const { renderRelativeDate } = useFormatter();
  const { openModal } = useModals();
  const { data: threads, isPending } = useAiAssistantThreads();
  const threadDelete = useAiAssistantThreadDelete();

  const confirmDelete = (thread: ApiAiAssistantThread) => {
    openModal(ConfirmationModal, {
      text: t("ai:assistant_thread_delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await threadDelete.mutateAsync(thread.id);
        onDeleted(thread.id);
      },
    });
  };

  if (isPending) {
    return (
      <FontAwesomeIcon icon={"fa-light fa-spinner-third fa-spin"} className={"text-secondary"} />
    );
  }

  if (!threads) return null;

  if (threads.length === 0) {
    return <Text className={"text-secondary"}>{t("ai:assistant_threads_empty")}</Text>;
  }

  return (
    <ul className={"flex flex-col gap-2"}>
      {threads.map((thread) => (
        <li
          key={thread.id}
          className={twMerge(
            "flex items-center gap-1 rounded-sm bg-light/50 pr-1 shadow-xs transition-colors hover:bg-light",
            thread.id === activeThreadId && "bg-light"
          )}
        >
          <button
            type={"button"}
            aria-current={thread.id === activeThreadId ? "true" : undefined}
            className={"flex min-w-0 flex-1 flex-col gap-1 px-3 py-2.5 text-left"}
            onClick={() => onOpen(thread.id)}
          >
            <Text className={"truncate"}>{thread.title}</Text>
            <Text size={"xs"} className={"font-mono uppercase tracking-wider text-secondary"}>
              {renderRelativeDate(thread.datetime_last_message)}
            </Text>
          </button>
          <ButtonIcon
            variant={"plain"}
            aria-label={t("ai:assistant_thread_delete")}
            icon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
            onClick={() => confirmDelete(thread)}
          />
        </li>
      ))}
    </ul>
  );
}
